import { useEffect, useRef } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { REVEAL_GROUP, RevealRest, RevealSlot } from './Reveal.tsx';

/**
 * Row primitives for every list: sessions, machines, runs, worktrees. One row height per
 * density (comfortable 40 px, compact 32 px, set by data-density), the same padding, the
 * same hover and selection, and a soft highlight when a live row changes.
 *
 *   <RowList label="Sessions">
 *     <ListRow lead={<StatusPill status="working" variant="plain" iconOnly />} onOpen={...}
 *       actions={<IconButton label="Stop" icon={<Square />} />} trail={<RowCell numeric>4 min</RowCell>}>
 *       <RowTitle title="Fix login race" meta="Qawl-Agents · studio" />
 *     </ListRow>
 *   </RowList>
 */

export interface RowListProps extends ComponentProps<'ul'> {
  /** Accessible name: "Sessions". */
  label: string;
}

export function RowList({ label, className, ...rest }: RowListProps) {
  // No rule under each row: rows group by rhythm and the hover fill, inset from the panel edge
  // by 4 px with the control radius (inner radius = card radius minus the inset).
  return <ul aria-label={label} className={cn('flex min-w-0 flex-col p-1', className)} {...rest} />;
}

export interface ListRowProps {
  /** Status or avatar at the start. */
  lead?: ReactNode;
  /** RowTitle and RowCells. */
  children: ReactNode;
  /** Hover-revealed actions at the end (always visible under touch). They take the trailing place, never draw over a cell. */
  actions?: ReactNode;
  /** The row's last cell (a time, a status), shown at rest and replaced by `actions` while they show. Without it, rows with actions reserve their width. */
  trail?: ReactNode;
  /** Makes the row a button that opens the item. */
  onOpen?: () => void;
  selected?: boolean;
  /** Change this when the row's live data changes: the row flashes softly once. */
  highlightKey?: string | number;
  /** Rows that grow past one line (a title and a meta line) keep the density height as a minimum. */
  multiline?: boolean;
  className?: string;
  'aria-label'?: string;
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function ListRow({ lead, children, actions, trail, onOpen, selected, highlightKey, multiline, className, 'aria-label': ariaLabel }: ListRowProps) {
  const ref = useRef<HTMLLIElement>(null);
  const first = useRef(true);
  // M4 cell tint: an --accent-soft underlay shows once and fades (opacity only, motion-tint);
  // under reduced motion its durations are 0, so nothing flashes.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const el = ref.current;
    if (!el || highlightKey == null) return;
    el.setAttribute('data-changed', '');
    const t = window.setTimeout(() => el.removeAttribute('data-changed'), 160);
    return () => window.clearTimeout(t);
  }, [highlightKey]);

  const inner = (
    <>
      {lead != null && <span className="flex shrink-0 items-center">{lead}</span>}
      <span className="flex min-w-0 flex-1 items-center gap-(--list-row-gap)">{children}</span>
      {actions != null ? <RevealRest>{trail}</RevealRest> : trail}
    </>
  );
  const layout = cn(
    'flex w-full min-w-0 items-center gap-(--list-row-gap) px-3 text-left',
    multiline ? 'min-h-(--list-row-height) py-1.5' : 'h-(--list-row-height)',
  );

  return (
    <li
      ref={ref}
      aria-label={ariaLabel}
      className={cn(
        REVEAL_GROUP,
        'motion-tint relative flex min-w-0 items-center rounded-control transition-colors duration-(--duration-fast)',
        selected ? 'selected-tint' : 'hover:bg-(--list-row-hover)',
        className,
      )}
    >
      {onOpen ? (
        <button type="button" onClick={onOpen} aria-current={selected || undefined} className={cn(layout, '-outline-offset-2')}>
          {inner}
        </button>
      ) : (
        <div className={layout}>{inner}</div>
      )}
      {actions != null && <RevealSlot className="absolute right-(--list-row-padding-x)">{actions}</RevealSlot>}
    </li>
  );
}

export interface RowTitleProps {
  title: ReactNode;
  /** One muted line under the title: project, machine, branch. */
  meta?: ReactNode;
  /** Ended or archived items read muted. */
  muted?: boolean;
  className?: string;
}

export function RowTitle({ title, meta, muted, className }: RowTitleProps) {
  return (
    <span className={cn('flex min-w-0 flex-1 flex-col', className)}>
      <span className={cn('truncate text-ui', muted ? 'text-fg-muted' : 'text-fg')}>{title}</span>
      {/* On a selected row's fill, subtle text drops under 4.5:1: one step stronger there. */}
      {meta != null && <span className="truncate text-caption text-fg-subtle [[aria-current]_&]:text-fg-muted">{meta}</span>}
    </span>
  );
}

export interface RowCellProps {
  children: ReactNode;
  /** Right aligned, tabular numbers. */
  numeric?: boolean;
  /** Fixed width class, for example "w-20". Columns line up when every row uses the same. */
  width?: string;
  /** Hide below this breakpoint. */
  hideBelow?: 'sm' | 'md' | 'lg';
  className?: string;
}

const HIDE = { sm: 'hidden sm:flex', md: 'hidden md:flex', lg: 'hidden lg:flex' };

export function RowCell({ children, numeric, width, hideBelow, className }: RowCellProps) {
  return (
    <span
      className={cn(
        'min-w-0 shrink-0 items-center truncate text-caption text-fg-muted',
        hideBelow ? HIDE[hideBelow] : 'flex',
        numeric && 'justify-end text-right num',
        width,
        className,
      )}
    >
      {children}
    </span>
  );
}
